import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, interval, Observable, of, Subject} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError, concatMap, filter, finalize, map, pluck, tap} from 'rxjs/operators';
import {differenceInMinutes} from 'date-fns';

export const parseJwt = (token): any => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
    return JSON.parse(jsonPayload);
};

export function tokenToUser(token: string): User {
    const claims = parseJwt(token);
    claims.exp = claims.exp * 1000;
    return {...claims.user, exp: new Date(claims.exp)};
}

export function authHeader(token: string): any {
    return {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        })
    };
}

export interface User {
    name: string;
    uid: string;
    role: string;
    email: string;
    halls: string[];
    exp: Date;
    roles: string[];
}

export const REFRESH_TOKEN_KEY = 'NGX-RTAPP-AUTH_REFRESH_TOKEN';
export const ACCESS_TOKEN_KEY = 'NGX-RTAPP-AUTH_ACCESS_TOKEN';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    public tokenRenew$ = new Subject<void>();
    // TODO - error handling in sign-in flow
    private user$ = new BehaviorSubject<User>(null);
    private authApi = `./auth-api`;

    constructor(private router: Router, private http: HttpClient) {
        this.loadUserFromSessionToken();
        this.liveRenew();
    }

    user(): User {
        return this.user$.getValue();
    }

    getUser$(): Observable<User> {
        return this.user$.asObservable();
    }

    setCompound(compound: any) {
        sessionStorage.setItem('RTAPP_DEV_COMPOUND', compound);
    }

    getCompound() {
        if (window.location.hostname === 'localhost') {
            return sessionStorage.getItem('RTAPP_DEV_COMPOUND') ?? 'BOTH';
        }
        const domain = window.location.hostname;
        if (domain.indexOf('-klnt') > -1) {
            return 'KLNT';
        }
        if (domain.indexOf('-mos') > -1) {
            return 'MOS';
        }
        return 'BOTH';

    }

    getToken(): Promise<string> {
        const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
        return Promise.resolve(token);
    }

    getRefreshToken(): string {
        return sessionStorage.getItem(REFRESH_TOKEN_KEY);
    }

    getAccessToken(): string {
        return sessionStorage.getItem(ACCESS_TOKEN_KEY);
    }

    ssoSignIn(token: string): Observable<any> {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
        this.loadUserFromSessionToken();
        return this.http.get(`${this.authApi}/obtain-refresh-token`, authHeader(token))
            .pipe(
                pluck('refresh_token'),
                tap((t: string) => sessionStorage.setItem(REFRESH_TOKEN_KEY, t))
            );
    }

    renewToken(uid: string): Observable<any> {
        const token = sessionStorage.getItem(REFRESH_TOKEN_KEY);
        return this.http.post(`${this.authApi}/renew`, {
            userId: uid, refresh_token: token
        }).pipe(
            pluck('access_token'),
            tap((t: string) => sessionStorage.setItem(ACCESS_TOKEN_KEY, t)),
            tap(() => this.tokenRenew$.next()),
            map((t: string) => tokenToUser(t)),
            catchError(() => of({})),
            tap((u: User) => this.user$.next(u)),
        );
    }

    getLoginPath() {
        const compound = this.getCompound();
        if (compound === 'MOS') {
            return '/login-mos';
        }
        if (compound === 'KLNT') {
            return '/login-klnt';
        }
        return '/login';
    }

    signOut(): void {
        this.http.post(`${this.authApi}/sign-out`, {
            userId: this.user$.getValue().uid,
            refresh_token: this.getRefreshToken()
        }, authHeader(this.getAccessToken()))
            .pipe(
                concatMap(() => this.router.navigateByUrl(this.getLoginPath())),
                finalize(() => {
                    this.user$.next({} as User);
                    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
                    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
                })
            ).subscribe();
    }

    isLoggedIn(): boolean {
        return !!this.user$.getValue()?.uid;
    }

    private loadUserFromSessionToken(): void {
        const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
        if (!token) {
            this.user$.next({} as User);
            return;
        }
        const user = tokenToUser(token);
        if (user.exp.getTime() < (new Date()).getTime()) {
            this.renewToken(user.uid).subscribe();
        } else {
            this.user$.next(user);
        }
    }

    private liveRenew(): void {
        interval(60 * 1000).pipe(
            filter(() => this.isLoggedIn()),
            tap(() => {
                if (!!this.user() && !!this.user().exp) {
                    const diffInMin = differenceInMinutes(this.user().exp.getTime(), (new Date()).getTime());
                    if (diffInMin < 15) {
                        this.renewToken(this.user().uid).subscribe();
                    }
                } else {
                    this.signOut();
                }
            })
        ).subscribe();
    }

}
